import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface CustomOffer {
  slotTime: string;
  offerText: string;
  capacity: number;
}

const CreateSlotsScreen: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingType = location.state?.bookingType || "grooming";

  // STEP 1: Calendar States (May 2026)
  const [rangeStartDay, setRangeStartDay] = useState<number | null>(null);
  const [rangeEndDay, setRangeEndDay] = useState<number | null>(null);

  // STEP 2: Time & Slot parameters
  const [startTime, setStartTime] = useState<string>("09:00");
  const [endTime, setEndTime] = useState<string>("17:00");
  const [slotDuration, setSlotDuration] = useState<string>("60");
  const [bufferTime, setBufferTime] = useState<number>(15);
  const [isBufferEnabled, setIsBufferEnabled] = useState<boolean>(true);
  const [defaultCapacity, setDefaultCapacity] = useState<number>(1);
  
  // Controls structural multi-step flow progression
  const [isConfigSaved, setIsConfigSaved] = useState<boolean>(false);

  // STEP 3: Custom unique slot modifiers overrides
  const [slotConfigs, setSlotConfigs] = useState<CustomOffer[]>([]);
  const [activeConfigSlot, setActiveConfigSlot] = useState<string | null>(null);
  const [tempOfferText, setTempOfferText] = useState<string>(" ");
  const [tempCapacity, setTempCapacity] = useState<number>(1);

  // Static Calendar Metadata configuration for May 2026
  const currentMonthYear = "May 2026";
  const daysInMonth = 31;
  const startDayOffset = 5; // May 1st, 2026 is a Friday

  const handleDayClick = (day: number) => {
    // Reset steps 2 and 3 down the pipeline when date changes
    setIsConfigSaved(false);
    setActiveConfigSlot(null);

    if (bookingType === "pet-taxi") {
      setRangeStartDay(day);
      setRangeEndDay(day);
      return;
    }

    if (!rangeStartDay || (rangeStartDay && rangeEndDay)) {
      setRangeStartDay(day);
      setRangeEndDay(null);
    } else if (rangeStartDay && !rangeEndDay) {
      if (day < rangeStartDay) {
        setRangeStartDay(day);
      } else {
        setRangeEndDay(day);
      }
    }
  };

  // Helper parsing parameters
  const timeToMinutes = (timeStr: string): number => {
    const [hrs, mins] = timeStr.split(":").map(Number);
    return hrs * 60 + mins;
  };

  const minutesToTimeStr = (totalMinutes: number): string => {
    const hrs = Math.floor(totalMinutes / 60) % 24;
    const mins = totalMinutes % 60;
    const ampm = hrs >= 12 ? "PM" : "AM";
    const displayHrs = hrs % 12 === 0 ? 12 : hrs % 12;
    const displayMins = mins < 10 ? `0${mins}` : mins;
    return `${displayHrs}:${displayMins} ${ampm}`;
  };

  // Compute operational grid matrix array explicitly after config is unlocked and committed
  const generatedSlotsPreview = useMemo(() => {
    if (!rangeStartDay || !isConfigSaved) return [];
    const slots: string[] = [];
    const startRaw = timeToMinutes(startTime);
    const endRaw = timeToMinutes(endTime);
    const step = parseInt(slotDuration, 10);
    const buffer = isBufferEnabled ? bufferTime : 0;

    let current = startRaw;
    while (current + step <= endRaw) {
      slots.push(`${minutesToTimeStr(current)} - ${minutesToTimeStr(current + step)}`);
      current += step + buffer;
    }
    return slots;
  }, [startTime, endTime, slotDuration, bufferTime, isBufferEnabled, rangeStartDay, isConfigSaved]);

  const handleSaveIndividualSlotConfig = () => {
    if (!activeConfigSlot) return;
    const existingIdx = slotConfigs.findIndex(o => o.slotTime === activeConfigSlot);
    const newConfig: CustomOffer = {
      slotTime: activeConfigSlot,
      offerText: tempOfferText.trim(),
      capacity: tempCapacity
    };

    if (existingIdx > -1) {
      const updatedConfigs = [...slotConfigs];
      updatedConfigs[existingIdx] = newConfig;
      setSlotConfigs(updatedConfigs);
    } else {
      setSlotConfigs(prev => [...prev, newConfig]);
    }
    setActiveConfigSlot(null);
    setTempOfferText("");
  };

  const handleFinalSubmitAllThings = () => {
    alert(
      `🎉 Matrix Complete! All configurations updated and saved successfully!\n` +
      `Dates Selected: ${rangeStartDay} ${rangeEndDay && rangeEndDay !== rangeStartDay ? `to ${rangeEndDay}` : ""} May 2026\n` +
      `Total Slots Generated Per Day: ${generatedSlotsPreview.length}`
    );
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        
        {/* Screen Header */}
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gray-50">
          <div>
            <button 
              onClick={() => navigate(-1)}
              className="text-sm text-gray-600 font-medium hover:text-[#234F1E] flex items-center gap-1 mb-1 cursor-pointer"
            >
              ← Back to Dashboard
            </button>
            <h2 className="text-2xl font-bold text-[#234F1E]">
              Slot Configurator: <span className="capitalize">{bookingType.replace("-", " ")}</span>
            </h2>
          </div>
          <span className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 bg-[#234F1E] text-white rounded-lg">
            {bookingType} Module
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6">
          
          {/* STEP 1: CALENDAR VIEW SECTION */}
          <div className="lg:col-span-1 space-y-5">
            <div>
              <h3 className="font-bold text-gray-800 text-lg">1. Choose Operational Date</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {bookingType === "pet-taxi" 
                  ? "Select a single target service day." 
                  : "Click a start date and an end date to define the range."}
              </p>
            </div>

            <div className="border border-gray-200 bg-gray-50 p-4 rounded-2xl">
              <div className="text-center font-bold text-sm text-gray-700 mb-3">{currentMonthYear}</div>
              
              <div className="grid grid-cols-7 text-center text-[11px] font-bold text-gray-400 mb-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => <div key={d}>{d}</div>)}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: startDayOffset }).map((_, i) => <div key={i}></div>)}

                {Array.from({ length: daysInMonth }).map((_, idx) => {
                  const day = idx + 1;
                  const isStart = rangeStartDay === day;
                  const isEnd = rangeEndDay === day;
                  const isInRange = rangeStartDay && rangeEndDay && day > rangeStartDay && day < rangeEndDay;

                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleDayClick(day)}
                      className={`h-10 w-10 text-xs font-bold rounded-xl flex items-center justify-center mx-auto transition cursor-pointer ${
                        isStart || isEnd 
                          ? "bg-[#6DBE45] text-white shadow-sm font-black scale-105"
                          : isInRange 
                          ? "bg-emerald-100 text-[#234F1E]" 
                          : "bg-white text-gray-700 border border-gray-100 hover:border-[#6DBE45]"
                      }`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>

            {rangeStartDay ? (
              <div className="bg-emerald-50 border border-emerald-100 p-3.5 rounded-xl text-xs text-[#234F1E] font-medium">
                📍 <strong>Selected Schedule Context:</strong> {rangeStartDay} May 2026 
                {rangeEndDay && rangeEndDay !== rangeStartDay ? ` to ${rangeEndDay} May 2026` : ""}
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-100 p-3 rounded-xl text-xs text-amber-800 font-medium">
                ⚠️ Please pick a calendar date context to build operational shifts.
              </div>
            )}
          </div>

          {/* RIGHT PANELS WORKSPACE: PIPELINE STEPS 2 & 3 */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* STEP 2: PARAMETERS CONFIGURATION (Visible only after Step 1 date select) */}
            {rangeStartDay && (
              <div className="bg-gray-50/50 border border-gray-100 p-5 rounded-2xl space-y-4">
                <h4 className="font-bold text-gray-800 text-sm border-b border-gray-200 pb-1.5">
                  2. Configuration Parameters
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="grid grid-cols-2 gap-2 md:col-span-1">
                    <div>
                      <label className="text-[11px] font-medium text-gray-500 block mb-1">Shift Start</label>
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => { setStartTime(e.target.value); setIsConfigSaved(false); }}
                        className="w-full bg-white border border-gray-200 rounded-lg px-2.5 py-2 text-xs outline-none focus:border-[#6DBE45]"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-medium text-gray-500 block mb-1">Shift End</label>
                      <input
                        type="time"
                        value={endTime}
                        onChange={(e) => { setEndTime(e.target.value); setIsConfigSaved(false); }}
                        className="w-full bg-white border border-gray-200 rounded-lg px-2.5 py-2 text-xs outline-none focus:border-[#6DBE45]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] font-medium text-gray-500 block mb-1">Interval Duration</label>
                    <select 
                      value={slotDuration}
                      onChange={(e) => { setSlotDuration(e.target.value); setIsConfigSaved(false); }}
                      className="w-full bg-white border border-gray-200 rounded-lg px-2.5 py-2 text-xs outline-none focus:border-[#6DBE45]"
                    >
                      <option value="30">30 Min (0.5 hr)</option>
                      <option value="45">45 Min</option>
                      <option value="60">60 Min (1.0 hr)</option>
                      <option value="90">90 Min (1.5 hr)</option>
                      <option value="120">120 Min (2.0 hr)</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[11px] font-medium text-gray-500">Buffer</label>
                        <input
                          type="checkbox"
                          checked={isBufferEnabled}
                          onChange={(e) => { setIsBufferEnabled(e.target.checked); setIsConfigSaved(false); }}
                          className="accent-[#6DBE45] h-3 w-3 cursor-pointer"
                        />
                      </div>
                      <input
                        type="number"
                        disabled={!isBufferEnabled}
                        value={bufferTime}
                        onChange={(e) => { setBufferTime(Math.max(0, parseInt(e.target.value, 10) || 0)); setIsConfigSaved(false); }}
                        className="w-full border border-gray-200 bg-white rounded-lg py-1.5 text-xs text-center"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-medium text-gray-500 block mb-1">Base Capacity</label>
                      <input
                        type="number"
                        min="1"
                        value={defaultCapacity}
                        onChange={(e) => { setDefaultCapacity(Math.max(1, parseInt(e.target.value, 10) || 1)); setIsConfigSaved(false); }}
                        className="w-full border border-gray-200 bg-white rounded-lg py-1.5 text-xs text-center font-semibold"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-2 flex justify-end">
                  <button
                    onClick={() => setIsConfigSaved(true)}
                    className={`px-6 py-2.5 rounded-xl font-bold text-xs transition shadow-sm cursor-pointer ${
                      isConfigSaved 
                        ? "bg-emerald-600 text-white opacity-80" 
                        : "bg-[#234F1E] text-white hover:opacity-90"
                    }`}
                  >
                    {isConfigSaved ? "✓ Configuration Saved" : "Save Configuration & Load Slots"}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: SLOTS DISPLAY (Visible only after Step 2 configuration is explicitly saved) */}
            {isConfigSaved && (
              <div className="space-y-4 animate-fade-in">
                <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                  <h3 className="font-bold text-gray-800 text-sm">3. Dynamic Generated Slots Matrix</h3>
                  <span className="text-[11px] bg-[#234F1E] text-white px-2 py-0.5 rounded font-medium">
                    {generatedSlotsPreview.length} Slots Computed / Day
                  </span>
                </div>

                {/* Offer and Multi-user Counter Override Modal Dialog Widget */}
                {activeConfigSlot && (
                  <div className="p-4 bg-amber-50/90 border border-amber-200 rounded-xl space-y-3 shadow-sm">
                    <div className="flex justify-between items-center">
                      <p className="text-xs font-bold text-amber-900">
                        Custom Matrix Override for: <span className="underline font-mono">{activeConfigSlot}</span>
                      </p>
                      <button onClick={() => setActiveConfigSlot(null)} className="text-xs text-gray-400 font-bold">✕</button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-white p-2.5 rounded-lg border border-amber-200 flex items-center justify-between">
                        <div>
                          <label className="text-[11px] font-bold text-gray-700 block">Slot Capacity</label>
                          <p className="text-[10px] text-gray-400">Max check-ins concurrent</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setTempCapacity(prev => Math.max(1, prev - 1))}
                            className="h-7 w-7 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 font-bold text-xs flex items-center justify-center cursor-pointer"
                          >
                            -
                          </button>
                          <span className="text-xs font-bold w-4 text-center">{tempCapacity}</span>
                          <button
                            type="button"
                            onClick={() => setTempCapacity(prev => prev + 1)}
                            className="h-7 w-7 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 font-bold text-xs flex items-center justify-center cursor-pointer"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-gray-700 block">Attach Special Offer</label>
                        <div className="flex gap-1.5">
                          <input
                            type="text"
                            placeholder="e.g. 15% OFF, Flat ₹100 Off"
                            value={tempOfferText}
                            onChange={(e) => setTempOfferText(e.target.value)}
                            className="flex-1 bg-white border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs outline-none"
                          />
                          <button
                            onClick={handleSaveIndividualSlotConfig}
                            className="bg-amber-600 text-white text-xs font-bold px-3 rounded-lg hover:bg-amber-700 cursor-pointer"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Render Core Generated Grid Blocks */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 max-h-[220px] overflow-y-auto pr-1">
                  {generatedSlotsPreview.map((slot) => {
                    const customConfig = slotConfigs.find(o => o.slotTime === slot);
                    const displayCapacity = customConfig ? customConfig.capacity : defaultCapacity;
                    const hasOffer = customConfig && customConfig.offerText;

                    return (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => {
                          setActiveConfigSlot(slot);
                          setTempOfferText(customConfig ? customConfig.offerText : "");
                          setTempCapacity(displayCapacity);
                        }}
                        className={`p-3 border text-left rounded-xl transition cursor-pointer flex flex-col justify-between ${
                          activeConfigSlot === slot 
                            ? "ring-2 ring-amber-500 bg-amber-50/40 border-amber-400"
                            : hasOffer 
                            ? "bg-amber-50/30 border-amber-200 hover:border-[#6DBE45]" 
                            : "bg-white border-gray-200 hover:border-[#6DBE45]"
                        }`}
                      >
                        <div className="w-full">
                          <span className="text-[11px] font-bold text-gray-800 block mb-1 font-mono">{slot}</span>
                          {hasOffer && (
                            <span className="inline-block text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded mb-1">
                              ★ {customConfig.offerText}
                            </span>
                          )}
                        </div>

                        <div className="w-full flex justify-between items-center mt-2 pt-1.5 border-t border-gray-100">
                          <span className="text-[9px] text-gray-400 font-medium">Available</span>
                          <span className="text-[10px] font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                            👤 {displayCapacity} {displayCapacity === 1 ? "User" : "Users"}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* MASTER SAVE BUTTON APPENDED DIRECTLY UNDERNEATH PREVIEW GRID */}
                <div className="pt-4 border-t border-gray-100 flex justify-end">
                  <button
                    onClick={handleFinalSubmitAllThings}
                    className="bg-[#6DBE45] hover:opacity-90 text-white font-bold text-sm px-8 py-3 rounded-xl transition shadow-sm cursor-pointer"
                  >
                    Save & Launch All Slots Matrix
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default CreateSlotsScreen;